import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Mail, Lock, Eye, EyeOff, AlertCircle, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Spinner } from "@/components/shared/ui";

export default function Login() {
  const { user, loading, login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError(t("enter_email_password"));
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message === "SESSION_EXPIRED" ? t("login_failed") : err.message || t("login_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 py-10">
      <div className="w-full max-w-md">
        <button
          onClick={() => setLanguage(language === "en" ? "ku" : "en")}
          className="mx-auto mb-6 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600"
        >
          <Globe className="h-3.5 w-3.5" /> {language === "en" ? "EN" : "کوردی"}
        </button>

        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--brand-light)]">
            <HardHat className="h-10 w-10 text-[var(--brand)]" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("construction")}</h1>
          <h2 className="text-3xl font-bold tracking-tight text-[var(--brand)]">{t("estimation_system")}</h2>
          <p className="mt-2 text-sm text-slate-500">{t("manage_estimations")}</p>
        </div>

        <form onSubmit={handleLogin} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h3 className="mb-6 text-xl font-semibold text-slate-900">{t("sign_in")}</h3>

          {error ? (
            <div data-testid="login-error-message" className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">{t("email")}</label>
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
            <Mail className="h-5 w-5 text-slate-400" />
            <input
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              className="h-12 flex-1 bg-transparent text-sm text-slate-900 outline-none"
              placeholder={t("enter_email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">{t("password")}</label>
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
            <Lock className="h-5 w-5 text-slate-400" />
            <input
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              className="h-12 flex-1 bg-transparent text-sm text-slate-900 outline-none"
              placeholder={t("enter_password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" data-testid="toggle-password-btn" onClick={() => setShowPassword((s) => !s)} className="p-1 text-slate-400">
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={submitting}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-[var(--brand)] text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-70"
          >
            {submitting ? <Spinner className="h-5 w-5" /> : t("sign_in")}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-slate-400">{t("app_version")}</p>
      </div>
    </div>
  );
}
